import type { CustomBlockPayload, MechanismType } from './types';

export type ParseBlockPatchResult =
  | { ok: true; payload: CustomBlockPayload }
  | { ok: false; reason: 'missing' | 'decode_error' | 'invalid_json' | 'invalid_schema' };

const HASH_KEY = 'patchBlockJson';
const ALLOWED_PATCH_MECHANISMS = new Set<MechanismType>([
  'socket',
  'bathroom_socket',
  'kitchen_socket',
  'switch',
  'switch_double',
]);

function objectValue(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function trimmedLabel(value: unknown, maxLength: number): string | undefined {
  const label = stringValue(value)?.trim().slice(0, maxLength);
  return label || undefined;
}

function getHashParam(hash: string): string | null {
  const source = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!source) return null;

  for (const part of source.split('&')) {
    const [rawKey, ...rawValueParts] = part.split('=');
    if (rawKey !== HASH_KEY) continue;
    return rawValueParts.join('=');
  }

  return null;
}

export function parseBlockPatchFromHash(hash: string): ParseBlockPatchResult {
  const encoded = getHashParam(hash);
  if (encoded === null) return { ok: false, reason: 'missing' };

  let jsonText: string;
  try {
    jsonText = decodeURIComponent(encoded);
  } catch {
    return { ok: false, reason: 'decode_error' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonText);
  } catch {
    return { ok: false, reason: 'invalid_json' };
  }

  const root = objectValue(parsed);
  if (
    !root ||
    root.type !== 'panelforge_block_patch' ||
    root.version !== 1 ||
    root.target !== 'custom_block_template'
  ) {
    return { ok: false, reason: 'invalid_schema' };
  }

  const set = objectValue(root.set);
  if (!set || (set.orientation !== 'horizontal' && set.orientation !== 'vertical')) {
    return { ok: false, reason: 'invalid_schema' };
  }
  if (!Array.isArray(set.mechanisms) || set.mechanisms.length < 1 || set.mechanisms.length > 4) {
    return { ok: false, reason: 'invalid_schema' };
  }

  const mechanisms: CustomBlockPayload['mechanisms'] = [];
  for (const rawMechanism of set.mechanisms) {
    const mechanism = objectValue(rawMechanism);
    const type = stringValue(mechanism?.type);
    if (!type || !ALLOWED_PATCH_MECHANISMS.has(type as MechanismType)) {
      return { ok: false, reason: 'invalid_schema' };
    }

    const label = trimmedLabel(mechanism?.label, 40);
    mechanisms.push({
      type: type as MechanismType,
      ...(label ? { label } : {}),
    });
  }

  const label = trimmedLabel(set.label, 80);
  return {
    ok: true,
    payload: {
      version: 1,
      ...(label ? { label } : {}),
      orientation: set.orientation,
      mechanisms,
    },
  };
}
